// ============================================================================
// rca.ts — el ANÁLISIS DE CAUSA RAÍZ, y por qué es un documento aparte
// ----------------------------------------------------------------------------
// `Investigacion` (eventos.ts) es el EXPEDIENTE DE UN HECHO: qué pasó, en qué
// apoyo de qué línea, qué se ve, qué se concluye y qué falta por verificar. Eso
// funciona y no se toca.
//
// Un análisis de causa raíz es otra cosa: **es el razonamiento sobre uno o
// varios hechos**. Y por eso vive fuera, con dos consecuencias que no son de
// diseño sino de utilidad:
//
//   1. LA RECURRENCIA. La causa raíz más cara de un parque es la que se repite.
//      El mismo conector fallando en tres apoyos de dos líneas distintas es UNA
//      causa raíz y TRES eventos. Si el análisis viviera dentro de la vista de
//      una línea, ese patrón sería invisible POR CONSTRUCCIÓN: nadie lo vería
//      jamás, no porque el sistema falle sino porque nunca formuló la pregunta.
//
//   2. EL EVENTO SIN DUEÑO. Un aviso de las 2 de la mañana todavía no tiene
//      línea ni apoyo identificados. `Investigacion` exige los dos, y con razón:
//      un hecho SÍ ocurre en un sitio. Pero exigirlos para poder ABRIR el
//      análisis obliga a inventar el apoyo — el mismo error que el contrato ya
//      evita en `Evidencia`, donde exigir inspección a las 99 fotos «obligaba a
//      inventar quién iba en la cuadrilla».
//
// Por eso `Investigacion` queda INTACTA y esto es un documento hermano, con el
// alcance por listas y todas opcionales. Cambio ADITIVO: nada se renombra, nada
// migra.
//
// LA REGLA QUE GOBIERNA ESTE ARCHIVO. Las listas son CERRADAS. No hay texto
// libre donde deba haber una categoría, porque una categoría inventada al vuelo
// no se puede agregar entre análisis, y agregar entre análisis es justamente lo
// que hace valiosa la recurrencia.
// ============================================================================
import { z } from 'zod';
import { Base, Id, Instante, Uid } from './comunes.ts';
// La escala de verosimilitud NO se redefine aquí: es la misma que ya usa
// `Investigacion`. Dos escalas con los mismos nombres divergen el día que
// alguien añade un valor a una sola, y entonces «alta» deja de significar lo
// mismo en dos pantallas del mismo informe. Un hecho, un dueño.
import { Verosimilitud } from './eventos.ts';

// ── LAS ESPINAS DEL ISHIKAWA ────────────────────────────────────────────────

/**
 * Las 6M clásicas (máquina, método, material, medio ambiente, mano de obra,
 * medición) NO se usan, y no por gusto: nacieron en un astillero, para un
 * PROCESO que se repite. Una línea de 66 kV no es un proceso — es un activo
 * lineal, distribuido, expuesto y de cincuenta años de vida.
 *
 * Tres decisiones que conviene poder defender:
 *
 *   · «MÁQUINA» NO EXISTE aquí. No hay máquina: hay componentes pasivos, y cada
 *     uno se degrada por una física distinta.
 *
 *   · «MANO DE OBRA» SE ELIMINA COMO ESPINA. Es la que invita a terminar el
 *     análisis en un nombre propio, que es la forma más común de arruinar un
 *     RCA. El error humano existe y es real, pero entra como PROCESO (montaje,
 *     inspección) y como REGLA (diseño, especificación) — nunca como persona.
 *
 *   · «MEDICIÓN» NO ES ESPINA, ES EJE TRANSVERSAL. Un error de medición no
 *     tumba una línea: tumba el ANÁLISIS. Va en la calidad de cada evidencia.
 *     Confundir «el dato estaba mal» con «el activo falló» lleva derecho a un
 *     informe que culpa al inventario en vez de a la línea.
 */
export const Espina = z.enum([
  'conductor',              // el cable: fatiga, recocido, corrosión, daño de tendido
  'conexiones_empalmes',    // juntas y conectores: resistencia de contacto, embalamiento térmico
  'aislamiento_herrajes',   // cadenas, grapas, aisladores: contorneo, perforación, desgaste
  'estructura_cimentacion', // el apoyo y su fundación: pandeo, corrosión, socavación
  'tierra_apantallamiento', // puesta a tierra e hilo de guarda: el camino del rayo
  'ambiente_clima',         // viento, lluvia, temperatura, salinidad, descarga atmosférica
  'vegetacion_servidumbre', // árboles e invasión de la franja. Ver la nota de abajo: ya NO carga terceros ni quemas
  'diseno_hipotesis',       // la línea se calculó con supuestos que quizá no valen
  'montaje_tendido',        // cómo se construyó: par de apriete, poleas, torsión
  'operacion_maniobra',     // recierres, sobrecargas, maniobras
  'inspeccion_mantenimiento', // lo que debió detectarse y no se detectó

  // ── Las cinco de 2026-08-07 (`99 §ADR-026`). ADITIVAS: ni una clave se
  //    renombró, así que ningún documento existente migra ni se rompe.
  'proteccion_control',     // relé, teleprotección, coordinación de ajustes, alimentación CC
  'terceros_accidentales',  // vehículo, excavación, tala, izaje: daño de un tercero SIN intención
  'acto_malicioso',         // atentado, hurto de conductor o de ángulos, sabotaje
  'fauna',                  // aves y otros animales: contorneo, nidos, excrementos
  'fuego',                  // quema bajo la línea: el aire ionizado y el hollín tumban el aislamiento
]);

/**
 * POR QUÉ LAS CINCO NUEVAS, y por qué NO se renombró ninguna de las once viejas.
 *
 * **`proteccion_control` era el hueco más grave frente a la práctica del sector.**
 * Hasta hoy la protección existía SOLO como barrera —algo que debió detener el
 * evento— y nunca como causa. La consecuencia era muda y grande: **un análisis
 * nuestro no podía concluir «la línea no falló: falló el relé»**, que es un
 * desenlace corriente. Un ajuste mal coordinado, un esquema de teleprotección
 * que no llegó o una alimentación de corriente continua caída no tenían dónde
 * alojarse.
 *
 * **`terceros_accidentales`, `acto_malicioso`, `fauna` y `fuego` salen de dentro
 * de «vegetación y servidumbre»**, donde vivían escondidos en un comentario. Un
 * atentado no es vegetación, y en el Caribe colombiano eso no es un matiz
 * académico: se separan porque **la acción que sale de cada uno es distinta** —de
 * la vegetación se sale podando, del tercero accidental señalizando y coordinando
 * obras, del acto malicioso con vigilancia y con denuncia, y del fuego con
 * control de quemas—. Una familia que mezcla las cuatro produce un plan de
 * acción que no sirve para ninguna.
 *
 * ⚠️ **`vegetacion_servidumbre` NO se renombró ni se partió: se ESTRECHÓ.** La
 * clave es la misma y los documentos viejos siguen siendo válidos, pero desde el
 * 2026-08-07 significa solo vegetación e invasión de la franja. Un expediente
 * anterior a esa fecha puede tener ahí dentro un tercero o una quema, y eso es un
 * hecho fechado, no un error que haya que ir a corregir.
 */

/**
 * El estado de una espina. **NINGUNO DE LOS CUATRO ES UNA APROBACIÓN.**
 *
 * Y falta uno a propósito: NO existe «no aplica». Es el agujero por donde se
 * cuela el descarte cómodo — se marca «no aplica» sin mirar y la espina
 * desaparece del análisis sin que nadie haya descartado nada. Si de verdad no
 * aplica, es `descartada` y hay que decir con qué evidencia.
 */
export const EstadoEspina = z.enum([
  'descartada',    // la evidencia es INCOMPATIBLE con esta familia. Exige evidencia enlazada.
  'abierta',       // compatible con lo observado; sigue viva
  'sostenida',     // la evidencia apunta activamente aquí
  'no_evaluable',  // falta el dato. Exige decir CUÁL falta y quién lo tiene.
]);

export const EvaluacionEspina = z.object({
  espina: Espina,
  estado: EstadoEspina,
  /** Por qué está en ese estado. Obligatorio: un estado sin motivo no se puede auditar. */
  motivo: z.string().min(1).max(2000),
  /**
   * Evidencias que sostienen el estado. Para `descartada` y `sostenida` es
   * OBLIGATORIO al menos una — lo hace cumplir `nucleo/rca.js`, no este esquema,
   * porque la regla es de método y debe estar probada.
   */
  evidenciaIds: z.array(Id).default([]),
  /** Para `no_evaluable`: qué dato exacto falta y quién lo tiene. */
  datoQueFalta: z.string().max(500).optional(),
});

// ── LOS 5 PORQUÉS, CON ESCALERA DE NIVELES ──────────────────────────────────

/**
 * El método de los 5 porqués es famoso por lo fácil que es hacerlo mal: se para
 * en la primera respuesta cómoda. La escalera lo impide, porque nombra dónde
 * estás.
 *
 * ⚠️ UNA CADENA QUE TERMINA EN `mecanismo_fisico` NO ES CAUSA RAÍZ. Describe
 * física, no gestión — y sobre la física no se puede actuar. «El conector se
 * corroyó» no es una causa raíz: es lo que pasó. La causa raíz está en por qué
 * ESE conector, en ESA función, sin ESE control.
 */
export const NivelPorque = z.enum([
  'efecto',            // L1 · lo que se ve: la línea se abrió
  'modo_falla',        // L2 · qué pieza y cómo: el conector perdió continuidad
  'mecanismo_fisico',  // L3 · por qué falla así: corrosión galvánica + ciclado térmico
  'condicion',         // L4 · por qué existía aquí: herraje fuera de su función, sin par registrado
  'regla',             // L5 · qué regla lo permitió: la especificación no exigía inhibidor
]);

export const EslabonPorque = z.object({
  nivel: NivelPorque,
  enunciado: z.string().min(1).max(1000),
  /**
   * Con qué se sostiene este eslabón. Un porqué sin evidencia es una opinión
   * encadenada a otra opinión, y cinco de esas no son un análisis.
   */
  evidenciaIds: z.array(Id).default([]),
  /**
   * La cadena se corta aquí por falta de dato. Es preferible declararlo a
   * inventar el último eslabón, que es como se fabrica una causa raíz falsa.
   */
  cortadaPorFaltaDeDato: z.string().max(500).optional(),
});

export const CadenaPorques = z.object({
  id: Id,
  /** De qué espina cuelga esta cadena. */
  espina: Espina,
  eslabones: z.array(EslabonPorque).min(1).max(12),
});

// ── EL ÁRBOL DE CAUSAS Y EL ANÁLISIS DE BARRERAS ────────────────────────────

/**
 * Cómo se relaciona una causa con su consecuencia. La distinción importa: dos
 * causas NECESARIAS que se suman describen un evento que se pudo evitar
 * cortando cualquiera de las dos; una causa SUFICIENTE por sí sola, no.
 */
export const TipoArista = z.enum([
  'necesaria',   // sin ella el efecto no ocurre; con ella sola, tampoco basta
  'suficiente',  // por sí sola produce el efecto
  'contribuye',  // agrava o acelera, sin ser condición
]);

/**
 * LAS BARRERAS — la pregunta que suele valer más que la causa misma:
 * ¿qué defensa debió detener esto, y por qué no lo hizo?
 *
 * Un evento que atraviesa cinco barreras no tiene una causa raíz: tiene cinco
 * fallos de defensa, y arreglar solo la causa deja las otras cuatro abiertas.
 */
export const Barrera = z.enum([
  'apantallamiento',        // hilo de guarda: debió interceptar el rayo
  'puesta_a_tierra',        // debió evacuar la corriente sin contorneo inverso
  'aislamiento',            // la cadena debió soportar la sobretensión
  'distancia_seguridad',    // el despeje debió impedir el contacto
  'poda_servidumbre',       // la vegetación debió estar controlada
  'inspeccion_visual',      // el recorrido debió ver el defecto
  'termografia',            // la termografía debió detectar el punto caliente
  'proteccion_electrica',   // el relé debió despejar antes del daño
  'recierre',               // el recierre debió no insistir sobre falta permanente
  'mantenimiento_preventivo', // el plan debió intervenir antes
  'control_calidad_montaje',  // la recepción de obra debió rechazarlo
  'especificacion_diseno',    // la especificación debió exigirlo
  'gestion_repuestos',      // debió haber la pieza correcta disponible
  /**
   * CONTENCIÓN DE LA FALLA (`99 §ADR-026`). La pregunta que ninguna de las trece
   * anteriores hacía: **¿por qué cayeron seis apoyos y no uno?** Confiabilidad y
   * contención son cosas distintas — que la línea falle es un suceso, que la
   * falla se propague en cascada es otro, y se defienden con medidas distintas
   * (retenidas, apoyos de anclaje cada N vanos, herrajes de rotura controlada).
   * Un evento con esta barrera ausente no cambia la causa: cambia el TAMAÑO.
   */
  'contencion_falla',
]);

export const EstadoBarrera = z.enum([
  'ausente',       // nunca existió
  'inefectiva',    // existía y no funcionó
  'no_aplicada',   // existía, era aplicable, y no se aplicó
  'funciono',      // actuó: limitó el daño
  'no_evaluable',  // no consta si existía o actuó
]);

export const NodoCausa = z.object({
  id: Id,
  enunciado: z.string().min(1).max(1000),
  /** `null` en la raíz del árbol, que es el efecto observado. */
  padreId: Id.nullable(),
  tipoArista: TipoArista.optional(),
  nivel: NivelPorque,
  espina: Espina.optional(),
  evidenciaIds: z.array(Id).default([]),
  /** Qué defensa debió cortar esta rama, y qué le pasó. */
  barrera: z.object({
    cual: Barrera,
    estado: EstadoBarrera,
    detalle: z.string().max(1000),
  }).optional(),
});

// ── HIPÓTESIS, Y LO QUE LAS TUMBARÍA ────────────────────────────────────────

export const HipotesisRca = z.object({
  id: Id,
  enunciado: z.string().min(1).max(1000),
  espina: Espina,
  verosimilitud: Verosimilitud,
  sustento: z.string().min(1).max(2000),
  /**
   * ⚠️ EL CAMPO QUE HACE QUE ESTO SEA CIENCIA Y NO OPINIÓN.
   *
   * Qué evidencia REFUTARÍA esta hipótesis. Una hipótesis que ninguna evidencia
   * puede tumbar no es una hipótesis: es una creencia. Es obligatorio, y es la
   * diferencia entre un análisis y una narración convincente.
   */
  queLaRefutaria: z.string().min(1).max(1000),
  evidenciaIds: z.array(Id).default([]),
  /**
   * `true` si TODO su sustento es meteorológico. `nucleo/rca.js` topa estas
   * hipótesis en `baja`: que hubiera viento el día de la falla no prueba que el
   * viento la causara, y el clima es la correlación más tentadora que existe.
   */
  sustentoSoloClimatico: z.boolean().default(false),

  /**
   * QUÉ SE HIZO para ponerla a prueba. El compañero de `queLaRefutaria`, y sin él
   * aquel campo era una promesa: se declaraba qué la tumbaría y nadie iba a
   * mirarlo nunca.
   */
  queSeHizo: z.string().max(1000).optional(),
  /**
   * CÓMO QUEDÓ tras la prueba. Es lo que exige la séptima condición de cierre
   * (`99 §ADR-026`) para cada hipótesis RIVAL.
   *
   * `no_concluyente` cuenta como cerrada **a propósito**, y no es una puerta
   * trasera: la condición no obliga a tener un veredicto, obliga a que alguien
   * haya ido a mirar y haya escrito qué pasó. Exigir un veredicto sí o sí
   * fabricaría certeza donde no la hay, que es justo lo que este método existe
   * para impedir. Lo que ya no se puede es dejar la rival en silencio.
   */
  resultado: z.enum([
    'resistio',        // se puso a prueba y la hipótesis siguió en pie
    'refutada',        // la prueba la tumbó
    'no_concluyente',  // se intentó y no se pudo concluir. Honesto, y cuenta.
  ]).optional(),
});

// ── LO QUE NO SE PUEDE AFIRMAR ──────────────────────────────────────────────

/**
 * El bloque más honesto del expediente, y el que un informe convincente omite.
 * Va impreso: un análisis que no declara sus límites parece más fuerte y es más
 * frágil.
 */
export const Ausencia = z.object({
  que: z.string().min(1).max(500),
  porQue: z.string().min(1).max(1000),
  quienLoTiene: z.string().max(300).optional(),
  estado: z.enum(['pendiente', 'solicitado', 'recibido', 'no_disponible']).default('pendiente'),
});

// ── ACCIONES (CAPA) ─────────────────────────────────────────────────────────

export const Accion = z.object({
  id: Id,
  tipo: z.enum(['correctiva', 'preventiva']),
  que: z.string().min(1).max(1000),
  /** Sobre qué barrera actúa. Una acción que no cierra ninguna barrera es un deseo. */
  barrera: Barrera.optional(),
  responsable: z.string().max(300).optional(),
  plazo: z.string().max(100).optional(),
  estado: z.enum(['propuesta', 'aprobada', 'en_curso', 'cerrada', 'descartada']).default('propuesta'),
});

// ── EL DOCUMENTO ────────────────────────────────────────────────────────────

export const EstadoAnalisis = z.enum([
  'abierto',        // se está investigando
  'en_revision',    // el ingeniero lo está cerrando
  'cerrado',        // tiene causa raíz declarada y firmada
  'sin_conclusion', // se cierra SIN causa raíz. Es un final válido y honesto.
]);

/**
 * La FORMA del análisis, sin la regla de alcance.
 *
 * Se nombra aparte —y no es cosmético— porque `AnalisisCausa` lleva un `.refine`
 * encima, y un esquema con `refine` ya no es un objeto: no se le puede pedir
 * `.partial()` ni `.pick()`. Y hace falta poder pedírselo, porque las ediciones
 * llegan por PARTES (`ParteDeAnalisis`, abajo) y hasta hoy no las validaba nadie.
 */
const FormaAnalisis = Base.extend({
  tipo: z.literal('analisis_causa'),
  /** Código legible para el informe («RCA-2026-004»). No es la identidad. */
  codigo: z.string().min(1).max(40),
  titulo: z.string().min(1).max(300),
  estado: EstadoAnalisis.default('abierto'),
  abiertoEn: Instante,

  /**
   * EL ALCANCE, y aquí está la razón de existir de este documento.
   *
   * Las tres listas son opcionales por separado, pero el `refine` de abajo
   * exige que haya AL MENOS UNA cosa — o, si no hay ninguna, el motivo escrito.
   * Un análisis sobre nada no es un análisis; pero un análisis sobre un evento
   * cuyo apoyo aún no se ha identificado SÍ lo es, y debe poder abrirse sin
   * inventar el apoyo.
   */
  alcance: z.object({
    lineaIds: z.array(Id).default([]),
    apoyoIds: z.array(Id).default([]),
    investigacionIds: z.array(Id).default([]),
    /** Por qué todavía no hay activo señalado. Se rellena solo si las tres listas van vacías. */
    sinActivoIdentificado: z.string().max(500).optional(),
  }),

  /**
   * LA TABLA DE DESCARTES. Se guardan TODAS, siempre, incluso las que nadie ha
   * mirado — `nucleo/rca.js` las completa como `no_evaluable`. Una espina que
   * desaparece cuando falta el dato se lee como «eso ya no aplica».
   */
  espinas: z.array(EvaluacionEspina).default([]),
  cadenas: z.array(CadenaPorques).default([]),
  arbol: z.array(NodoCausa).default([]),
  hipotesis: z.array(HipotesisRca).default([]),
  ausencias: z.array(Ausencia).default([]),
  acciones: z.array(Accion).default([]),

  /**
   * La causa raíz DECLARADA. Opcional a propósito: durante días —a veces
   * semanas— el estado normal de un análisis es no tenerla.
   *
   * La marca una PERSONA. `nucleo/rca.js` calcula qué condiciones se cumplen y
   * cuáles no, y las imprime; no marca nunca. Es la misma línea que el resto del
   * producto: el motor mide, el ingeniero decide.
   */
  causaRaiz: z.object({
    nodoId: Id,
    enunciado: z.string().min(1).max(1000),
    declaradaPor: Uid,
    declaradaEn: Instante,
    /** Las condiciones que NO se cumplían al declararla. Se guardan: es la defensa del informe. */
    condicionesNoCumplidas: z.array(z.string()).default([]),
  }).optional(),

  /**
   * LAS CAUSAS RAÍZ, EN PLURAL (`99 §ADR-026`). Sustituye en la práctica al campo
   * de arriba, que se queda **de solo lectura** para no migrar nada.
   *
   * POR QUÉ. El motor ya avisaba —desde antes de que nadie mirara una norma— de
   * que «este evento atravesó N defensas: corregir solo la causa deja las otras
   * abiertas», y el formulario obligaba a comprimir todo en una sola línea. El
   * sistema afirmaba una cosa y exigía la contraria. En una salida por contorneo,
   * la puesta a tierra deficiente Y la especificación que no la exigía son dos
   * causas raíz: elegir una y fundir la otra en prosa la saca del expediente como
   * nodo con evidencia, y con ella se va la posibilidad de contarla el día que
   * exista la consulta de recurrencia.
   *
   * Se cambió AHORA y no después por una razón de coste: con cero causas
   * declaradas en producción esto es cambiar un formulario; con una sola ya
   * escrita, es migrar un documento firmado.
   *
   * ⚠️ **DOS CAMPOS PARA EL MISMO HECHO ES LA TRAMPA `estado`/`cerrado`** que este
   * mismo archivo documenta más abajo, y por eso lleva candado: `ParteDeAnalisis`
   * RECHAZA un parche que traiga los dos a la vez, y quien lee no elige — usa
   * `causasDeclaradas()` de `nucleo/rca.js`, que es el dueño único de la
   * precedencia. Si la pantalla y el informe resolvieran cada uno por su cuenta,
   * acabarían enseñando causas distintas del mismo expediente.
   */
  causasRaiz: z.array(z.object({
    nodoId: Id,
    enunciado: z.string().min(1).max(1000),
    /**
     * Qué papel tiene esta causa, con la tipología de IEC 62740:2015, cláusula 4
     * —verificada en el texto público, no de memoria—. La distinción NO es
     * cosmética: manda sobre qué se puede prometer al corregirla.
     */
    tipo: z.enum([
      /** Caso a): es la única. */
      'unica',
      /** Caso b): son varias, y eliminar CUALQUIERA de ellas evita el evento. */
      'multiple',
      /**
       * Caso c): eliminarla cambia la PROBABILIDAD de que ocurra, pero puede no
       * evitarlo. Corregir solo ésta y prometer que no se repite es el error.
       */
      'contribuyente',
    ]),
    declaradaPor: Uid,
    declaradaEn: Instante,
    condicionesNoCumplidas: z.array(z.string()).default([]),
  })).default([]),

  /** Límites del análisis, impresos en el informe. Se declara; no se omite. */
  limitaciones: z.array(z.string().max(1000)).default([]),
  /**
   * ⚠️ REDUNDANTE CON `estado`, Y ATADO A ÉL POR CONTRATO (`TODO-56`).
   *
   * Este booleano existe porque **las reglas de Firestore lo miran**: la regla de
   * `analisis` permite actualizar solo mientras `cerrado == false`. Un enum no se
   * puede consultar tan barato desde una regla, así que el booleano se queda.
   *
   * Pero dos representaciones del mismo hecho divergen en cuanto alguien escribe
   * una y olvida la otra, y aquí las dos consecuencias son malas y silenciosas:
   * con `estado: 'cerrado'` y `cerrado: false`, la pantalla dice cerrado y **la
   * base deja seguir editando el razonamiento que ya se firmó**; al revés, el
   * análisis se ve abierto y no se puede tocar, sin que nada explique por qué.
   *
   * Hoy no choca porque NADA pone `cerrado` en verdadero. Se ata ahora, antes de
   * que exista el botón de cerrar — después habría documentos con las dos
   * verdades ya escritas, y eso ya no se arregla con una regla.
   */
  cerrado: z.boolean().default(false),
});

/** Los dos estados TERMINALES: el análisis ya no se edita. */
const ESTADOS_TERMINALES: readonly string[] = ['cerrado', 'sin_conclusion'];

/** `estado` y `cerrado` dicen lo mismo, o el documento no es válido. */
const coherenteElCierre = (estado: string | undefined, cerrado: boolean | undefined): boolean =>
  cerrado === ESTADOS_TERMINALES.includes(String(estado));

export const AnalisisCausa = FormaAnalisis.refine(
  (a) => coherenteElCierre(a.estado, a.cerrado),
  {
    path: ['cerrado'],
    message: 'El estado del análisis y la marca de cerrado se contradicen. Los estados «cerrado» y '
           + '«sin conclusión» son finales: los dos exigen `cerrado: true`, y cualquier otro exige '
           + '`false`. Si divergen, la pantalla y las reglas de la base dicen cosas distintas del '
           + 'mismo documento — y una de las dos deja editar un razonamiento ya firmado.',
  },
).refine(
  (a) => a.alcance.lineaIds.length > 0
      || a.alcance.apoyoIds.length > 0
      || a.alcance.investigacionIds.length > 0
      || Boolean(a.alcance.sinActivoIdentificado),
  {
    message: 'Un análisis necesita alcance: al menos una línea, un apoyo o una investigación — '
           + 'o, si el evento aún no tiene activo identificado, el motivo escrito. '
           + 'Lo que no vale es dejarlo vacío en silencio.',
  },
);

/**
 * UNA PARTE del análisis, para guardar una edición.
 *
 * POR QUÉ EXISTE. `crearAnalisis` validaba contra el contrato antes de escribir;
 * `guardarParte` NO validaba nada — mandaba el parche directo a la base. Así que
 * el documento nacía protegido y **todas las ediciones posteriores no**, que es
 * justo al revés de lo que hace falta: el análisis se crea vacío en un segundo y
 * se edita durante semanas.
 *
 * Es `strict` a propósito. Una clave que no esté en esta lista se RECHAZA en vez
 * de escribirse: hoy un nombre de campo mal escrito entraba en el documento sin
 * que nadie se enterara, y quedaba ahí para siempre —basura silenciosa en un
 * documento que se firma—.
 *
 * Los campos de identidad y propiedad (`id`, `orgId`, `creadoPor`, `creadoEn`,
 * `codigo`, `tipo`, `abiertoEn`) NO están, y no es un olvido: no se editan. Las
 * reglas de Firestore lo impiden además por su lado (`noTocaReservados`), pero
 * una defensa que solo vive en el servidor da un error opaco; ésta dice cuál es
 * el campo y por qué.
 */
export const ParteDeAnalisis = FormaAnalisis.pick({
  titulo: true,
  estado: true,
  alcance: true,
  espinas: true,
  cadenas: true,
  arbol: true,
  hipotesis: true,
  ausencias: true,
  acciones: true,
  causaRaiz: true,
  causasRaiz: true,
  limitaciones: true,
  cerrado: true,
}).partial().strict().superRefine((p, ctx) => {
  // EL CANDADO DE LAS DOS CAUSAS. Un parche que traiga el campo viejo y el nuevo
  // a la vez deja el documento diciendo dos cosas del mismo hecho, y es
  // exactamente el fallo silencioso de `estado`/`cerrado`: la pantalla enseña
  // una causa y el informe imprime otra, sin que nada explique por qué.
  if (p.causaRaiz !== undefined && p.causasRaiz !== undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['causasRaiz'],
      message: 'No se pueden escribir a la vez la causa raíz vieja (una sola) y la lista de causas. '
             + 'La lista es la que vale desde el 2026-08-07; el campo viejo se conserva solo para '
             + 'leer lo ya declarado. Manda una o la otra.',
    });
  }
  // En un parche PARCIAL no se puede comprobar la pareja contra el documento
  // entero, así que se exige que viajen JUNTAS — pero SOLO cuando el cambio
  // toca el CIERRE.
  //
  // ⚠️ La primera versión de esta regla exigía la pareja en CUALQUIER cambio de
  // estado, y rompía el botón de declarar la causa raíz, que manda
  // `estado: 'en_revision'` a secas. Lo cazó la prueba de los parches reales, y
  // por eso existe: endurecer una escritura tiene el riesgo CONTRARIO al que se
  // está tapando — dejar fuera algo que la pantalla sí manda.
  //
  // Pasar a un estado NO terminal no necesita mandar `cerrado`: el documento se
  // está pudiendo editar, luego ya era `false`. Un análisis cerrado no se puede
  // reabrir por esta vía porque las reglas de la base ni siquiera dejan
  // escribirlo.
  const terminalNuevo = p.estado !== undefined && ESTADOS_TERMINALES.includes(p.estado);
  const cierraBooleano = p.cerrado === true;

  if (terminalNuevo && p.cerrado !== true) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['cerrado'],
      message: 'Para cerrar hay que mandar también `cerrado: true`: las reglas de la base miran ese '
             + 'booleano, no el estado. Con uno solo, la pantalla diría cerrado y la base seguiría '
             + 'dejando editar el razonamiento ya firmado.',
    });
  }
  if (cierraBooleano && !terminalNuevo) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['estado'],
      message: 'Al marcar `cerrado: true` hay que mandar el estado terminal que lo justifica: '
             + '«cerrado» (con causa raíz) o «sin conclusión» (cierre honesto sin ella).',
    });
  }
  if (p.cerrado === false && terminalNuevo) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['cerrado'],
      message: 'Un estado terminal con `cerrado: false` deja el documento diciendo dos cosas.',
    });
  }
});

export type ParteDeAnalisis = z.infer<typeof ParteDeAnalisis>;

// ── LAS ACCIONES CAPA, COMO DOCUMENTOS PROPIOS ──────────────────────────────

/**
 * Una acción correctiva o preventiva, en su PROPIA colección.
 *
 * POR QUÉ NO VIVE DENTRO DEL ANÁLISIS, aunque `AnalisisCausa.acciones` exista y
 * se quede ahí vacío: **dentro de un array las reglas de Firestore no pueden
 * distinguir «cerrar una acción» de «reescribir el razonamiento»**. Las dos
 * llegan como una escritura sobre el mismo documento. Y un análisis tiene que
 * poder congelarse el día que se firma mientras sus acciones siguen vivas
 * durante meses — que es exactamente el ciclo real del mantenimiento. Con las
 * acciones dentro, o se congela todo o no se congela nada.
 *
 * ADITIVO: `AnalisisCausa.acciones` no se toca ni se migra. Nunca lo escribió
 * ninguna pantalla, así que no hay un solo documento que convertir.
 *
 * LO QUE ESTE ESQUEMA **NO** HACE CUMPLIR, y es deliberado: las reglas duras
 * («no se cierra sin prueba», «una correctiva cerrada tiene que decir qué
 * barrera cerró») viven en `nucleo/rca.js`, probadas. Un esquema que rechaza
 * hace DESAPARECER el documento entero en la lectura siguiente; el núcleo
 * SEÑALA y deja el documento visible con su defecto a la vista. Aquí solo se
 * exige lo que es cierto siempre.
 */
export const AccionCapa = Base.extend({
  tipo: z.literal('accion_capa'),
  /** De qué análisis cuelga. Una acción huérfana no se puede auditar. */
  analisisId: Id,
  clase: z.enum(['correctiva', 'preventiva']),
  /** Qué se va a hacer. En imperativo y concreto: es lo que alguien ejecuta. */
  que: z.string().min(1).max(1000),
  /**
   * Sobre qué barrera actúa. OPCIONAL para guardar, y no por comodidad: con el
   * árbol vacío y trece barreras en una lista cerrada, obligarla empuja a elegir
   * una al azar con tal de poder guardar — el mismo atajo que vaciaba el
   * Ishikawa y por el que se eliminó «no aplica». Se vuelve obligatoria para
   * CERRAR una correctiva: un deseo puede existir sin barrera; un hecho
   * consumado tiene que decir qué defensa dejó cerrada.
   */
  barrera: Barrera.optional(),
  responsable: z.string().max(300).optional(),
  plazo: z.string().max(100).optional(),
  estado: z.enum(['propuesta', 'aprobada', 'en_curso', 'cerrada', 'descartada']).default('propuesta'),

  // ── El cierre, y su prueba ────────────────────────────────────────────────
  cerradaPor: Uid.optional(),
  cerradaEn: Instante.optional(),
  /** Evidencias que demuestran que se hizo. */
  evidenciaIds: z.array(Id).default([]),
  /**
   * Cómo se comprobó, cuando no hay evidencia enlazada: acta, orden de trabajo,
   * informe de cuadrilla.
   *
   * Existe porque el contrato admite a propósito un análisis SIN investigación
   * en el alcance (el evento de las 2 de la mañana sin activo identificado), y
   * las evidencias solo se pueden traer de las investigaciones del alcance.
   * Exigir evidencia enlazada dejaría esas acciones sin poder cerrarse NUNCA.
   * No se igualan las dos pruebas: el motor distingue cuál es, y el informe
   * imprime «cerrada, probada solo con una nota escrita».
   */
  comoSeComprobo: z.string().max(1000).optional(),
  /** Por qué se descarta. Sin motivo, descartar es esconder. */
  motivoDescarte: z.string().max(1000).optional(),
});

export type AccionCapa = z.infer<typeof AccionCapa>;

/** Lo editable de una acción. Mismo razonamiento que `ParteDeAnalisis`. */
export const ParteDeAccion = AccionCapa.pick({
  clase: true, que: true, barrera: true, responsable: true, plazo: true,
  estado: true, cerradaPor: true, cerradaEn: true, evidenciaIds: true,
  comoSeComprobo: true, motivoDescarte: true,
}).partial().strict();

export type ParteDeAccion = z.infer<typeof ParteDeAccion>;

// ── EL SONDEO DE CLIMA ──────────────────────────────────────────────────────

/**
 * Una consulta a IDEAM, congelada.
 *
 * NO es una caché: es un HECHO FECHADO. Si dentro de un año IDEAM corrige una
 * serie, el informe firmado tiene que seguir mostrando lo que se consultó el día
 * que se firmó. Por eso las reglas de Firestore lo hacen inmutable — se crea, no
 * se actualiza.
 */
export const SondeoClima = Base.extend({
  tipo: z.literal('sondeo_clima'),
  analisisId: Id,
  consultadoEn: Instante,
  /**
   * QUÉ PUNTO se consultó, y cuándo se dijo que ocurrió el evento.
   *
   * Sin esto un sondeo guardado no se puede interpretar: un análisis puede
   * abarcar varios apoyos de varias líneas, y la nota dice «a N km del punto»
   * sin que conste cuál era el punto. Un dato meteorológico que no sabe de dónde
   * es no sirve para defender nada — y este documento existe para defender.
   *
   * Va en la base PRIVADA, nunca en el repositorio público: es la coordenada de
   * un activo de cliente, igual que las de `apoyos`.
   */
  punto: z.object({
    lat: z.number().min(-90).max(90),
    lon: z.number().min(-180).max(180),
    /** La hora del evento tal como se tecleó, que es lo que gobernó la ventana. */
    ocurrioEn: Instante,
  }),
  /** La ventana de tiempo que se pidió, alrededor del evento. */
  desde: Instante,
  hasta: Instante,
  estacion: z.object({
    codigo: z.string().min(1),
    nombre: z.string().min(1),
    municipio: z.string().optional(),
    departamento: z.string().optional(),
    lat: z.number(),
    lon: z.number(),
    /** ⚠️ Va SIEMPRE junto al valor, nunca al pie. Una estación a 40 km no dice lo mismo. */
    distancia_km: z.number().nonnegative(),
  }),
  /**
   * `fechaobservacion` de IDEAM viene SIN zona horaria. Que se interprete como
   * hora de Colombia es una inferencia NUESTRA, y se declara como tal.
   */
  interpretacionHoraria: z.string().min(1),
  series: z.array(z.object({
    variable: z.enum(['temperatura', 'precipitacion', 'viento_velocidad', 'viento_direccion', 'presion']),
    conjunto: z.string().min(1),   // el identificador del conjunto en datos.gov.co
    unidad: z.string().min(1),
    n: z.number().int().nonnegative(),
    valores: z.array(z.object({ t: z.string(), v: z.number() })).default([]),
  })).default([]),
  /** Último dato que IDEAM tenía publicado al consultar. Se MIDE, no se supone. */
  ultimoDatoDisponible: z.string().optional(),
  /** `true` si el evento es posterior al último dato: no hay clima que traer todavía. */
  fueraDeVentana: z.boolean().default(false),
  /** El texto que se pinta. Lo redacta el núcleo, no la pantalla. */
  nota: z.string().max(2000),
});

export type Espina = z.infer<typeof Espina>;
export type EstadoEspina = z.infer<typeof EstadoEspina>;
export type NivelPorque = z.infer<typeof NivelPorque>;
export type Barrera = z.infer<typeof Barrera>;
export type EstadoBarrera = z.infer<typeof EstadoBarrera>;
export type AnalisisCausa = z.infer<typeof AnalisisCausa>;
export type SondeoClima = z.infer<typeof SondeoClima>;
export type EvaluacionEspina = z.infer<typeof EvaluacionEspina>;
export type CadenaPorques = z.infer<typeof CadenaPorques>;
export type NodoCausa = z.infer<typeof NodoCausa>;
export type HipotesisRca = z.infer<typeof HipotesisRca>;
